/**
 * RouteAccessGuard - Blocks page access when user lacks menu permission.
 * Uses Access Control API (menu_keys) to check if user can access the current route.
 * Redirects to dashboard when access is denied.
 */
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { accessControlService } from '../../services/accessControlService';
import {
  credentialVaultCredentialModuleAllowed,
  credentialVaultVaultSettingsAllowed,
  routeAllowedByMenuAccess,
  isCredentialVaultCredentialModulePath,
  isCredentialVaultVaultSettingsPath,
  normalizeAccessPath,
  getRequiredSectionAction,
} from '../../utils/routeAccessConfig';
import { canDo, hasLoadedSectionActions } from '../../utils/roleAccess';
import { getResolvedLandingPage } from '../../utils/userSettings';
import LoadingSpinner from './LoadingSpinner/LoadingSpinner';

const MENU_KEYS_CACHE_MS = 60000; // 1 min
let menuKeysCache = null;
let menuRoutePathsCache = null;
let menuKeysCacheTime = 0;

export default function RouteAccessGuard({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(null);
  const [menuAccessRevision, setMenuAccessRevision] = useState(0);

  useEffect(() => {
    const invalidate = () => {
      menuKeysCache = null;
      menuRoutePathsCache = null;
      menuKeysCacheTime = 0;
      setMenuAccessRevision((n) => n + 1);
    };
    window.addEventListener('menu-access-updated', invalidate);
    return () => window.removeEventListener('menu-access-updated', invalidate);
  }, []);

  useEffect(() => {
    let cancelled = false;
    /** Do not call setAllowed(null) here — it would unmount the entire <Outlet /> (spinner replaces children)
     *  on every pathname change and tear down CredentialVaultCryptoProvider, wiping the in-memory DEK. */
    const pathname = location.pathname || '';
    const normalizedPath = normalizeAccessPath(pathname);

    const check = async () => {
      try {
        let menuKeys;
        let routePaths;
        if (menuKeysCache && Date.now() - menuKeysCacheTime < MENU_KEYS_CACHE_MS) {
          menuKeys = menuKeysCache;
          routePaths = menuRoutePathsCache;
        } else {
          const res = await accessControlService.getMyAccess();
          const rawKeys = res?.data?.data?.menuKeys;
          menuKeys = Array.isArray(rawKeys) ? rawKeys : [];
          const rawPaths = res?.data?.data?.routePaths;
          routePaths = Array.isArray(rawPaths) ? rawPaths : [];
          menuKeysCache = menuKeys;
          menuRoutePathsCache = routePaths;
          menuKeysCacheTime = Date.now();
        }

        let hasAccess = false;

        if (isCredentialVaultVaultSettingsPath(normalizedPath)) {
          hasAccess = credentialVaultVaultSettingsAllowed(menuKeys);
          if (!hasAccess && credentialVaultCredentialModuleAllowed(menuKeys, routePaths)) {
            hasAccess = true;
          }
        } else if (isCredentialVaultCredentialModulePath(normalizedPath)) {
          hasAccess = credentialVaultCredentialModuleAllowed(menuKeys, routePaths);
        } else {
          hasAccess = routeAllowedByMenuAccess(menuKeys, routePaths, normalizedPath);
        }

        const sectionNeed = getRequiredSectionAction(normalizedPath);
        if (hasAccess && sectionNeed && hasLoadedSectionActions()) {
          hasAccess = canDo(sectionNeed.section, sectionNeed.action);
        }
        if (!cancelled) {
          setAllowed(hasAccess);
          if (!hasAccess) {
            const fallback = await getResolvedLandingPage();
            navigate(fallback, { replace: true });
          }
        }
      } catch (err) {
        if (!cancelled) {
          setAllowed(true);
        }
      }
    };

    check();
    return () => { cancelled = true; };
  }, [location.pathname, navigate, menuAccessRevision]);

  if (allowed === null) {
    return <LoadingSpinner label="Checking access..." />;
  }

  if (allowed === false) {
    return null;
  }

  return children;
}
